// Resume-protocol wire types and builders (Phase 5 Step 2).
//
// Handshake on every WebSocket connect:
//   client  →  resume { epochId?, lastSeenSeq?, fresh? }
//   server  →  replay { envelopes: ServerMessage[] }     (durable-only, seq-ASC)
//                 OR
//             snapshot { epochId, lastSeq, ...joinedFields }
//
// Decision rules (replay vs snapshot) live in server/src/player-resume.ts.
// This file is shape-only.
//
// Envelope is inlined here — same precedent as workshop-messages.ts and
// market-messages.ts — because shared/messages.ts re-exports from this
// file, and a reciprocal runtime import would be a cycle. `import type`
// from messages.ts is safe (erased at runtime).

import type { EquippedItemReadout } from "./equip-messages.ts";
import type { MarketView } from "./market.ts";
import type {
  InventorySlot,
  PlayerState,
  ResourceNode,
  ServerMessage,
  SkillEntry,
} from "./messages.ts";
import type { WorkshopState } from "./workshop-messages.ts";

// Matches the shape in shared/messages.ts. Optional seq/eventId are the
// Phase 5 Step 3 durable augmentation — handshake meta-envelopes below
// never carry them themselves, but the nested durable envelopes in
// `replay.payload.envelopes` do.
type Envelope<T extends string, P> = {
  type: T;
  ts: number;
  payload: P;
  seq?: number;
  eventId?: string;
};

// -----------------------------------------------------------------------
// Client → Server
// -----------------------------------------------------------------------

// `fresh: true` is a client-side explicit signal ("I have no persisted
// state, don't bother checking seq/epoch"). Omitting both epochId AND
// lastSeenSeq is equivalent to fresh — the handshake coordinator treats
// missing fields as "cannot replay, must snapshot".
export type ClientResume = Envelope<
  "resume",
  { epochId?: string; lastSeenSeq?: number; fresh?: boolean }
>;

// -----------------------------------------------------------------------
// Server → Client
// -----------------------------------------------------------------------

// Durable-only envelopes ordered by seq ASC. Client applies each in
// order and advances its localStorage lastSeenSeq as it consumes them.
// Ephemeral events are never replayed — they're re-derivable from a
// snapshot or superseded by current state.
export type ServerReplay = Envelope<"replay", { envelopes: ServerMessage[] }>;

// Full bootstrap. Payload mirrors the existing `joined` envelope plus
// epochId + lastSeq so the client can seed localStorage for the next
// session's resume attempt. The joined-equivalent fields are duplicated
// rather than wrapped so the on-wire shape stays flat and trivial to
// spread into client state.
export type ServerSnapshot = Envelope<
  "snapshot",
  {
    epochId: string;
    lastSeq: number;
    playerId: string;
    zoneId: string;
    players: PlayerState[];
    inventory: InventorySlot[];
    nodes: ResourceNode[];
    workshop: WorkshopState;
    skills: SkillEntry[];
    credits: number;
    market: MarketView;
    // Phase 7 Block C — currently-equipped weapon, or null. Lets the
    // client hydrate the equipped state on first connect / resume so
    // the inventory modal's E badge + equipment slot render correctly
    // before any equippedItemUpdated push arrives.
    equippedItem: EquippedItemReadout | null;
  }
>;

// -----------------------------------------------------------------------
// Builders
// -----------------------------------------------------------------------

// Conditional spreads keep absent fields genuinely absent under
// exactOptionalPropertyTypes (not `epochId: undefined`).
export const buildResumeMessage = (
  epochId?: string,
  lastSeenSeq?: number,
  fresh?: boolean,
): ClientResume => ({
  type: "resume",
  ts: Date.now(),
  payload: {
    ...(epochId !== undefined ? { epochId } : {}),
    ...(lastSeenSeq !== undefined ? { lastSeenSeq } : {}),
    ...(fresh !== undefined ? { fresh } : {}),
  },
});

export const buildReplayMessage = (envelopes: ServerMessage[]): ServerReplay => ({
  type: "replay",
  ts: Date.now(),
  payload: { envelopes },
});

// The snapshot body is a single SnapshotBody object rather than a dozen
// positional arguments — new builders use objects to stay readable
// when the field list grows.
export type SnapshotBody = {
  playerId: string;
  zoneId: string;
  players: PlayerState[];
  inventory: InventorySlot[];
  nodes: ResourceNode[];
  workshop: WorkshopState;
  skills: SkillEntry[];
  credits: number;
  market: MarketView;
  equippedItem: EquippedItemReadout | null;
};

export const buildSnapshotMessage = (
  epochId: string,
  lastSeq: number,
  body: SnapshotBody,
): ServerSnapshot => ({
  type: "snapshot",
  ts: Date.now(),
  payload: { epochId, lastSeq, ...body },
});
